import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    HiOutlinePlus, HiOutlineSearch, HiX, HiOutlineEmojiSad, HiOutlineTicket, HiOutlineDotsVertical, HiOutlineDuplicate,
    HiOutlineTrash, HiOutlineLockClosed, HiOutlinePencil, HiOutlineClipboardCopy, HiOutlineUsers, HiOutlineGlobe,
    HiOutlineKey, HiOutlineSparkles, HiOutlineRefresh, HiOutlineCalendar, HiOutlineCurrencyRupee, HiOutlineCheckCircle
} from 'react-icons/hi';
import { TbTruckDelivery } from 'react-icons/tb';
import { FaWhatsapp } from 'react-icons/fa';
import AuthenticationService from '../service/AuthenticationService';
import CouponService from '../service/CouponService';
import SidePanel from '../components/SidePanel';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { formatRupee } from '../components/Price';
import brand from '../brand';
import {
    COUPON_TYPES, SCOPES, AUDIENCES, STATUS_LABELS, couponValue, couponTerms, couponSentence, couponDates, formatDay, randomCode
} from '../util/coupons';
import { todayIso } from '../util/expenseTypes';
import '../css/admin.css'
import '../css/edit-item.css'
import '../css/coupons.css'

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'expired', label: 'Ended' },
    { key: 'off', label: 'Off' },
]

const AUDIENCE_ICONS = { ALL: <HiOutlineGlobe />, SELECTED: <HiOutlineUsers />, CODE: <HiOutlineKey /> }

const EMPTY_FORM = {
    name: '', code: '', description: '', discountType: 'PERCENT', discountValue: '', maxDiscount: '', minOrderAmount: '',
    audience: 'ALL', startsOn: '', endsOn: '', usageLimit: '', active: true, users: [], freeDelivery: false, appliesTo: 'ALL_ITEMS'
}

const CODE_PATTERN = /^[A-Z0-9]{4,24}$/

const serverMessage = (error, fallback) => (error && error.response && error.response.data && error.response.data.msg) || fallback

const digits = (value, decimals) => {
    const clean = String(value).replace(decimals ? /[^\d.]/g : /\D/g, '')
    if (!decimals) return clean
    const [whole, ...rest] = clean.split('.')
    return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole
}

const initial = (name) => (String(name || '?').trim()[0] || '?').toUpperCase()

const audienceChip = (c) => {
    if (c.audience === 'SELECTED') {
        const n = (c.users || []).length
        return `${n} ${n === 1 ? 'shopper' : 'shoppers'} · auto`
    }
    return c.audience === 'CODE' ? 'Code' : 'Everyone · auto'
}

const copyText = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text)
    return new Promise((resolve, reject) => {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(area)
        if (ok) resolve()
        else reject(new Error('copy failed'))
    })
}

const shareLink = (c) => {
    const site = String(brand.siteUrl || window.location.origin).replace(/\/+$/, '')
    const text = `🎁 Use code ${c.code} at ${brand.name} for ${couponSentence(c).replace(/, once per shopper\.$/, '')}.`
        + (c.endsOn ? ` Valid till ${formatDay(c.endsOn)}.` : '') + `\n${site}`
    return 'https://wa.me/?text=' + encodeURIComponent(text)
}

// ---------- ticket ----------

const Ticket = ({ coupon, preview, onOpen, menu, toggle, onCopy, flash }) => {
    const status = coupon.status || 'ACTIVE'
    const value = couponValue(coupon)
    const [amount, ...rest] = value.split(' ')
    const limit = Number(coupon.usageLimit) || 0
    const taken = Number(coupon.takenCount) || 0
    const used = Number(coupon.usedCount) || 0
    const Tag = preview ? 'div' : 'li'
    return (
        <Tag className={`mv-cpn-ticket is-${status.toLowerCase()}` + (preview ? ' is-preview' : '') + (flash ? ' is-flash' : '')}>
            <div className='mv-cpn-stub' aria-hidden={preview ? 'true' : undefined}>
                {coupon.discountType === 'NONE' && <TbTruckDelivery className='mv-cpn-stub-icon' aria-hidden='true' />}
                <span className='mv-cpn-value'>{amount}</span>
                <span className='mv-cpn-value-unit'>{rest.join(' ')}</span>
                {coupon.freeDelivery && coupon.discountType !== 'NONE' &&
                    <span className='mv-cpn-stub-tag'>+ free delivery</span>}
            </div>
            <div className='mv-cpn-body'>
                <div className='mv-cpn-head'>
                    {onOpen ?
                        <button type='button' className='mv-cpn-name' onClick={onOpen}>{coupon.name || 'Untitled coupon'}</button> :
                        <span className='mv-cpn-name'>{coupon.name || 'Coupon name'}</span>}
                    {!preview && <span className={`mv-cpn-status is-${status.toLowerCase()}`}>{STATUS_LABELS[status] || status}</span>}
                    {menu}
                </div>
                <div className='mv-cpn-code-row'>
                    <span className='mv-coupon-code mv-cpn-code'>{coupon.code || (preview ? 'AUTO-CODE' : '')}</span>
                    {onCopy &&
                        <button type='button' className='mv-link-btn mv-cpn-copy' onClick={onCopy} aria-label={`Copy code ${coupon.code}`}>
                            <HiOutlineClipboardCopy aria-hidden='true' /> Copy
                        </button>}
                </div>
                <p className='mv-cpn-terms'>{couponTerms(coupon)}</p>
                <div className='mv-cpn-meta'>
                    <span className='mv-cpn-chip'>{AUDIENCE_ICONS[coupon.audience]} {audienceChip(coupon)}</span>
                    <span className='mv-cpn-chip mv-cpn-chip-plain'><HiOutlineCalendar aria-hidden='true' /> {couponDates(coupon)}</span>
                </div>
                {!preview &&
                    <div className='mv-cpn-usage'>
                        {limit > 0 &&
                            <span className='mv-cpn-bar' aria-hidden='true'>
                                <span style={{ width: Math.min(100, (taken / limit) * 100) + '%' }} />
                            </span>}
                        <span className='mv-cpn-usage-text'>
                            {limit > 0 ? `${used} of ${limit} used` : `${used} ${used === 1 ? 'order' : 'orders'}`}
                            {Number(coupon.discountGiven) > 0 && <> · <strong>{formatRupee(coupon.discountGiven)}</strong> saved</>}
                        </span>
                        {toggle}
                    </div>}
            </div>
        </Tag>
    )
}

const ListSkeleton = () => (
    <ul className='mv-cpn-grid' aria-hidden='true'>
        {[0, 1, 2].map(i =>
            <li key={i} className='mv-cpn-ticket mv-cpn-ticket-skel'>
                <div className='mv-cpn-stub'><Skeleton width={56} height={30} /></div>
                <div className='mv-cpn-body'>
                    <Skeleton width='60%' height={20} />
                    <Skeleton width={110} height={24} radius='6px' />
                    <Skeleton width='80%' height={14} />
                    <Skeleton width='50%' height={14} />
                </div>
            </li>)}
    </ul>
)

const Tile = ({ icon, label, value, loading, tone }) => (
    <div className={'mv-cpn-tile ' + tone}>
        <span className='mv-cpn-tile-icon' aria-hidden='true'>{icon}</span>
        <span className='mv-cpn-tile-label'>{label}</span>
        {loading ? <Skeleton width='50%' height={30} /> : <span className='mv-cpn-tile-value'>{value}</span>}
    </div>
)

const ActiveSwitch = ({ checked, busy, onChange, label }) => (
    <label className={'mv-switch mv-cpn-switch' + (busy ? ' is-busy' : '')}>
        <input type='checkbox' checked={checked} disabled={busy} onChange={e => onChange(e.target.checked)} aria-label={label} />
        <span className='mv-switch-track' aria-hidden='true'><span className='mv-switch-thumb' /></span>
        <span className='mv-switch-label'>{checked ? 'On' : 'Off'}</span>
    </label>
)

// ---------- shopper picker (SELECTED coupons) ----------

const ShopperPicker = ({ selected, onChange }) => {
    const [all, setAll] = useState(null)
    const [failed, setFailed] = useState(false)
    const [search, setSearch] = useState('')

    useEffect(() => {
        AuthenticationService.getUserList()
            .then(res => setAll((Array.isArray(res.data) ? res.data : [])
                .filter(u => (u.role || 'USER') === 'USER' && u.status !== 'DISABLED')))
            .catch(() => setFailed(true))
    }, [])

    const chosen = new Set(selected.map(u => u.userId))
    const q = search.trim().toLowerCase()
    const matches = (all || [])
        .filter(u => !chosen.has(u.userId))
        .filter(u => !q || [u.name, u.email, u.phone].some(v => String(v || '').toLowerCase().includes(q)))
        .slice(0, 6)

    const add = (u) => {
        onChange([...selected, { userId: u.userId, name: u.name, email: u.email, phone: u.phone }])
        setSearch('')
    }

    return (
        <div className='mv-cpn-picker'>
            {selected.length > 0 &&
                <ul className='mv-cpn-picked' aria-label='Chosen shoppers'>
                    {selected.map(u =>
                        <li key={u.userId} className='mv-cpn-picked-chip'>
                            <span className='mv-user-avatar mv-role-customer' aria-hidden='true'>{initial(u.name)}</span>
                            <span className='mv-cpn-picked-name'>{u.name || u.email}</span>
                            <button type='button' className='mv-cpn-picked-remove' aria-label={`Remove ${u.name || u.email}`}
                                onClick={() => onChange(selected.filter(x => x.userId !== u.userId))}><HiX /></button>
                        </li>)}
                </ul>}
            <div className='mv-search'>
                <HiOutlineSearch aria-hidden='true' />
                <input type='search' className='mv-input' placeholder='Search shoppers by name, email or phone'
                    aria-label='Search shoppers' value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {failed && <p className='mv-field-error'>Could not load shoppers. Close and open the coupon to try again.</p>}
            {!failed && all === null && <Skeleton height={44} radius='10px' />}
            {all !== null && (q || selected.length === 0) &&
                <ul className='mv-cpn-results'>
                    {matches.length === 0 ?
                        <li className='mv-cpn-results-empty'>{q ? 'No shoppers match.' : 'No shoppers yet.'}</li> :
                        matches.map(u =>
                            <li key={u.userId}>
                                <button type='button' className='mv-cpn-result' onClick={() => add(u)}>
                                    <span className='mv-user-avatar mv-role-customer' aria-hidden='true'>{initial(u.name)}</span>
                                    <span className='mv-cpn-result-text'>
                                        <span className='mv-cpn-result-name'>{u.name || 'No name'}</span>
                                        <span className='mv-cpn-result-sub'>{[u.email, u.phone].filter(Boolean).join(' · ')}</span>
                                    </span>
                                    <HiOutlinePlus className='mv-cpn-result-add' aria-hidden='true' />
                                </button>
                            </li>)}
                </ul>}
        </div>
    )
}

// ---------- page ----------

// Admin-only Coupons page: % or ₹ off the items, applied automatically (everyone / chosen shoppers) or by code.
// Managers see a lock and the API is never called (a 403 would sign them out).
const Coupons = () => {
    const history = useHistory()
    const location = useLocation()
    const isAdmin = AuthenticationService.canManageSettings()

    const params = new URLSearchParams(location.search)
    const status = FILTERS.some(f => f.key === params.get('status')) ? params.get('status') : 'all'

    const [search, setSearch] = useState(params.get('q') || '')
    const [q, setQ] = useState(params.get('q') || '')
    const [list, setList] = useState(null)   // { coupons, totalCount, activeCount, usedThisMonth, discountThisMonth }
    const [listError, setListError] = useState(false)
    const listReq = useRef(0)

    const [menuId, setMenuId] = useState(null)
    const [busyId, setBusyId] = useState(null)
    const [editor, setEditor] = useState(null)  // { mode: 'new' | 'edit' | 'duplicate', coupon }
    const [form, setForm] = useState(EMPTY_FORM)
    const [formError, setFormError] = useState('')
    const [saving, setSaving] = useState(false)
    const [usedBy, setUsedBy] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [toast, setToast] = useState('')
    const [flashId, setFlashId] = useState(null)
    const nameRef = useRef(null)

    // ---- data ----

    useEffect(() => {
        const timer = setTimeout(() => setQ(search.trim()), 300)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        const next = new URLSearchParams(location.search)
        if (q) next.set('q', q)
        else next.delete('q')
        if (next.toString() !== new URLSearchParams(location.search).toString()) {
            history.replace({ pathname: location.pathname, search: next.toString() ? '?' + next.toString() : '' })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q])

    const load = useCallback(() => {
        if (!isAdmin) return
        const id = ++listReq.current
        setListError(false)
        CouponService.list({ status, q })
            .then(res => { if (id === listReq.current) setList((res && res.data && res.data.data) || { coupons: [] }) })
            .catch(() => { if (id === listReq.current) setListError(true) })
    }, [isAdmin, status, q])

    useEffect(() => { load() }, [load])

    useEffect(() => {
        if (menuId == null) return undefined
        const close = () => setMenuId(null)
        const onKey = (e) => { if (e.key === 'Escape') close() }
        document.addEventListener('click', close)
        document.addEventListener('keydown', onKey)
        return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey) }
    }, [menuId])

    useEffect(() => {
        if (flashId == null) return undefined
        const timer = setTimeout(() => setFlashId(null), 1600)
        return () => clearTimeout(timer)
    }, [flashId])

    const setStatus = (key) => {
        const next = new URLSearchParams(location.search)
        if (key === 'all') next.delete('status')
        else next.set('status', key)
        history.replace({ pathname: location.pathname, search: next.toString() ? '?' + next.toString() : '' })
    }

    // ---- actions ----

    const openEditor = (mode, coupon) => {
        setMenuId(null)
        setFormError('')
        setUsedBy(null)
        if (!coupon) {
            setForm(EMPTY_FORM)
        } else {
            setForm({
                name: mode === 'duplicate' ? `${coupon.name} (copy)`.slice(0, 80) : coupon.name,
                code: mode === 'duplicate' ? '' : coupon.code,
                description: coupon.description || '',
                discountType: coupon.discountType,
                discountValue: coupon.discountValue != null ? String(Number(coupon.discountValue)) : '',
                maxDiscount: coupon.maxDiscount != null ? String(Number(coupon.maxDiscount)) : '',
                minOrderAmount: coupon.minOrderAmount != null ? String(Number(coupon.minOrderAmount)) : '',
                audience: coupon.audience,
                startsOn: mode === 'duplicate' ? '' : (coupon.startsOn || ''),
                endsOn: mode === 'duplicate' ? '' : (coupon.endsOn || ''),
                usageLimit: coupon.usageLimit != null ? String(coupon.usageLimit) : '',
                active: mode === 'duplicate' ? true : coupon.active !== false,
                users: coupon.users || [],
                freeDelivery: !!coupon.freeDelivery,
                appliesTo: coupon.appliesTo || 'ALL_ITEMS',
            })
            if (mode === 'edit') {
                CouponService.redemptions(coupon.id)
                    .then(res => setUsedBy(res.data.data || []))
                    .catch(() => setUsedBy([]))
            }
        }
        setEditor({ mode, coupon: coupon || null })
    }

    const closeEditor = () => {
        if (saving) return
        setEditor(null)
    }

    useEffect(() => {
        if (editor && editor.mode !== 'edit' && nameRef.current) {
            const timer = setTimeout(() => nameRef.current && nameRef.current.focus({ preventScroll: true }), 260)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [editor])

    const field = (name, transform) => (e) => {
        const value = transform ? transform(e.target.value) : e.target.value
        setForm(f => ({ ...f, [name]: value }))
        setFormError('')
    }

    const validate = () => {
        if (!form.name.trim()) return 'Please give the coupon a name.'
        const value = Number(form.discountValue)
        if (form.discountType === 'NONE') {
            if (!form.freeDelivery) return 'Choose a discount or turn on free delivery.'
        } else if (form.discountType === 'PERCENT') {
            if (!form.discountValue || !Number.isInteger(value) || value < 1 || value > 90) return 'Percent off must be a whole number from 1 to 90.'
        } else if (!form.discountValue || !(value > 0)) {
            return 'Please enter an amount off above ₹0.'
        }
        const min = Number(form.minOrderAmount) || 0
        if (form.discountType === 'AMOUNT' && min > 0 && value > min) return "The amount off can't be more than the minimum order."
        if (form.code && !CODE_PATTERN.test(form.code)) return 'Codes use 4 to 24 letters and numbers, no spaces or symbols.'
        if (form.audience === 'CODE' && !form.code) return 'Type a code shoppers will enter, or tap Generate.'
        if (form.audience === 'SELECTED' && form.users.length === 0) return 'Choose at least one shopper.'
        if (form.startsOn && form.endsOn && form.endsOn < form.startsOn) return 'The end date is before the start date.'
        if (form.usageLimit && !(Number(form.usageLimit) >= 1)) return 'The usage limit must be at least 1, or left empty.'
        return ''
    }

    const save = (addAnother) => {
        const problem = validate()
        if (problem) {
            setFormError(problem)
            return
        }
        const id = editor && editor.mode === 'edit' ? editor.coupon.id : null
        const orNull = (v) => (v === '' || v == null ? null : Number(v))
        const payload = {
            name: form.name.trim(),
            code: form.code || null,
            description: form.description.trim() || null,
            discountType: form.discountType,
            discountValue: form.discountType === 'NONE' ? null : Number(form.discountValue),
            freeDelivery: form.freeDelivery,
            appliesTo: form.discountType === 'NONE' ? 'ALL_ITEMS' : form.appliesTo,
            maxDiscount: form.discountType === 'PERCENT' ? orNull(form.maxDiscount) : null,
            minOrderAmount: orNull(form.minOrderAmount),
            audience: form.audience,
            startsOn: form.startsOn || null,
            endsOn: form.endsOn || null,
            usageLimit: orNull(form.usageLimit),
            active: form.active,
            userIds: form.audience === 'SELECTED' ? form.users.map(u => u.userId) : [],
        }
        setSaving(true)
        setFormError('')
        CouponService.save(id, payload)
            .then(res => {
                const saved = res.data.data
                setToast(id ? 'Coupon saved' : `Coupon ${saved.code} is ready`)
                setFlashId(saved.id)
                load()
                if (addAnother) {
                    setForm(f => ({ ...EMPTY_FORM, discountType: f.discountType, audience: f.audience, freeDelivery: f.freeDelivery, appliesTo: f.appliesTo }))
                    setEditor({ mode: 'new', coupon: null })
                    if (nameRef.current) nameRef.current.focus()
                } else {
                    setEditor(null)
                }
            })
            .catch(err => setFormError(serverMessage(err, 'Could not save the coupon. Please try again.')))
            .finally(() => setSaving(false))
    }

    const toggleActive = (coupon, active) => {
        setBusyId(coupon.id)
        // show the change at once; put it back if the server refuses
        setList(l => l && ({ ...l, coupons: l.coupons.map(c => c.id === coupon.id ? { ...c, active } : c) }))
        CouponService.setActive(coupon.id, active)
            .then(res => {
                const saved = res.data.data
                setList(l => l && ({ ...l, coupons: l.coupons.map(c => c.id === saved.id ? { ...c, ...saved } : c) }))
                setToast(active ? `${coupon.code} is on` : `${coupon.code} is off`)
                load()
            })
            .catch(err => {
                setList(l => l && ({ ...l, coupons: l.coupons.map(c => c.id === coupon.id ? { ...c, active: !active } : c) }))
                setToast(serverMessage(err, 'Could not change the coupon. Please try again.'))
            })
            .finally(() => setBusyId(null))
    }

    const copy = (coupon) => {
        setMenuId(null)
        copyText(coupon.code).then(() => setToast(`Code ${coupon.code} copied`)).catch(() => setToast('Could not copy the code'))
    }

    const doDelete = () => {
        if (!confirmDelete) return
        setDeleting(true)
        CouponService.remove(confirmDelete.id)
            .then(() => {
                setToast('Coupon deleted')
                if (editor && editor.coupon && editor.coupon.id === confirmDelete.id) setEditor(null)
                setConfirmDelete(null)
                load()
            })
            .catch(err => setConfirmDelete(c => ({ ...c, error: serverMessage(err, 'Could not delete the coupon. Please try again.') })))
            .finally(() => setDeleting(false))
    }

    // ---- view ----

    const preview = useMemo(() => ({
        ...form,
        discountValue: form.discountValue === '' ? null : form.discountValue,
        status: form.startsOn && form.startsOn > todayIso() ? 'SCHEDULED' : 'ACTIVE',
        startsOn: form.startsOn || null,
        endsOn: form.endsOn || null,
    }), [form])

    if (!isAdmin) {
        return (
            <main className='mv-page mv-cpn'>
                <EmptyState icon={<HiOutlineLockClosed />} title='Only admins can manage coupons'
                    text='Coupons change what the shop earns, so they are kept to admins. Ask an admin if a coupon is needed.'
                    actionLabel='Back to dashboard' onAction={() => history.push('/dashboard')} />
            </main>
        )
    }

    const isEdit = editor && editor.mode === 'edit'
    const editing = isEdit ? editor.coupon : null
    const editorTitle = !editor ? '' : isEdit ? 'Edit coupon' : editor.mode === 'duplicate' ? 'Duplicate coupon' : 'New coupon'
    const coupons = (list && list.coupons) || []
    const filtered = status !== 'all' || !!q
    const clearFilters = () => { setSearch(''); setQ(''); setStatus('all') }

    const renderForm = () => (
        <form id='cpn-form' className='mv-form mv-cpn-form' onSubmit={(e) => { e.preventDefault(); save(false) }} noValidate>
            <div className='mv-cpn-preview' aria-label='Preview'>
                <span className='mv-cpn-preview-label'><HiOutlineSparkles aria-hidden='true' /> What shoppers see</span>
                <Ticket coupon={preview} preview />
                <p className='mv-cpn-sentence'>{couponSentence(preview)}</p>
            </div>

            {isEdit && Number(editing.usedCount) > 0 &&
                <p className='mv-alert mv-alert-info'>
                    Used on {editing.usedCount} {Number(editing.usedCount) === 1 ? 'order' : 'orders'}. Changes apply to new orders only.
                </p>}

            <div className='mv-field'>
                <label className='mv-label' htmlFor='cpn-name'>Name</label>
                <input id='cpn-name' ref={nameRef} className='mv-input' maxLength={80} placeholder='e.g. Diwali sale'
                    value={form.name} onChange={field('name')} />
            </div>

            <fieldset className='mv-cpn-fieldset'>
                <legend className='mv-label'>Discount</legend>
                <div className='mv-cpn-type is-three' role='radiogroup' aria-label='Discount type'>
                    {COUPON_TYPES.map(t => {
                        const locked = t.value === 'NONE' && !form.freeDelivery
                        return (
                            <label key={t.value} className={'mv-cpn-type-opt' + (form.discountType === t.value ? ' active' : '') + (locked ? ' is-locked' : '')}
                                title={locked ? 'Turn on free delivery to make a coupon with no discount' : undefined}>
                                <input type='radio' name='cpn-type' value={t.value} checked={form.discountType === t.value} disabled={locked}
                                    onChange={() => { setForm(f => ({ ...f, discountType: t.value })); setFormError('') }} />
                                {t.label}
                            </label>
                        )
                    })}
                </div>

                <div className='mv-cpn-toggle-row'>
                    <span className='mv-cpn-toggle-icon' aria-hidden='true'><TbTruckDelivery /></span>
                    <span className='mv-cpn-toggle-text'>
                        <strong>Free delivery</strong>
                        <small>{form.discountType === 'NONE' ? 'This coupon only waives the delivery charge' : 'Also waive the delivery charge'}</small>
                    </span>
                    <ActiveSwitch checked={form.freeDelivery} label='Free delivery'
                        onChange={(on) => {
                            setForm(f => ({ ...f, freeDelivery: on, discountType: !on && f.discountType === 'NONE' ? 'PERCENT' : f.discountType }))
                            setFormError('')
                        }} />
                </div>

                {form.discountType !== 'NONE' &&
                    <div className='mv-field'>
                        <span className='mv-label' id='cpn-scope-label'>Applies to</span>
                        <div className='mv-cpn-type' role='radiogroup' aria-labelledby='cpn-scope-label'>
                            {SCOPES.map(sc =>
                                <label key={sc.value} className={'mv-cpn-type-opt' + (form.appliesTo === sc.value ? ' active' : '')}>
                                    <input type='radio' name='cpn-scope' value={sc.value} checked={form.appliesTo === sc.value}
                                        onChange={() => { setForm(f => ({ ...f, appliesTo: sc.value })); setFormError('') }} />
                                    {sc.label}
                                </label>)}
                        </div>
                        <p className='mv-field-hint'>
                            {form.appliesTo === 'ONE_ITEM'
                                ? 'The discount goes to one piece of the most expensive item in the cart.'
                                : 'The discount is taken off the whole items total.'}
                        </p>
                    </div>}

                <div className='mv-form-grid mv-cpn-two'>
                    {form.discountType !== 'NONE' &&
                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='cpn-value'>{form.discountType === 'PERCENT' ? 'Percent off' : 'Amount off'}</label>
                        <div className={'mv-cpn-affix ' + (form.discountType === 'PERCENT' ? 'is-suffix' : 'is-prefix')}>
                            {form.discountType !== 'PERCENT' && <span aria-hidden='true'>₹</span>}
                            <input id='cpn-value' className='mv-input' inputMode='decimal' autoComplete='off'
                                placeholder={form.discountType === 'PERCENT' ? '10' : '100'} value={form.discountValue}
                                onChange={field('discountValue', v => digits(v, form.discountType !== 'PERCENT'))} />
                            {form.discountType === 'PERCENT' && <span aria-hidden='true'>%</span>}
                        </div>
                    </div>}
                    {form.discountType === 'PERCENT' &&
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='cpn-max'>Up to <span className='mv-label-optional'>(optional)</span></label>
                            <div className='mv-cpn-affix is-prefix'>
                                <span aria-hidden='true'>₹</span>
                                <input id='cpn-max' className='mv-input' inputMode='decimal' autoComplete='off' placeholder='No cap'
                                    value={form.maxDiscount} onChange={field('maxDiscount', v => digits(v, true))} />
                            </div>
                        </div>}
                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='cpn-min'>Min order <span className='mv-label-optional'>(optional)</span></label>
                        <div className='mv-cpn-affix is-prefix'>
                            <span aria-hidden='true'>₹</span>
                            <input id='cpn-min' className='mv-input' inputMode='decimal' autoComplete='off' placeholder='Any amount'
                                value={form.minOrderAmount} onChange={field('minOrderAmount', v => digits(v, true))} />
                        </div>
                        <p className='mv-field-hint'>Items only; delivery doesn't count.</p>
                    </div>
                </div>
            </fieldset>

            <fieldset className='mv-cpn-fieldset'>
                <legend className='mv-label'>Who can use it</legend>
                <div className='mv-cpn-audiences'>
                    {AUDIENCES.map(a =>
                        <label key={a.value} className={'mv-cpn-audience' + (form.audience === a.value ? ' active' : '')}>
                            <input type='radio' name='cpn-audience' value={a.value} checked={form.audience === a.value}
                                onChange={() => { setForm(f => ({ ...f, audience: a.value })); setFormError('') }} />
                            <span className='mv-cpn-audience-icon' aria-hidden='true'>{AUDIENCE_ICONS[a.value]}</span>
                            <span className='mv-cpn-audience-text'><strong>{a.label}</strong><small>{a.hint}</small></span>
                        </label>)}
                </div>
                {form.audience === 'SELECTED' &&
                    <ShopperPicker selected={form.users} onChange={(users) => { setForm(f => ({ ...f, users })); setFormError('') }} />}
            </fieldset>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='cpn-code'>
                    Code {form.audience !== 'CODE' && <span className='mv-label-optional'>(optional, made for you)</span>}
                </label>
                <div className='mv-cpn-code-input'>
                    <input id='cpn-code' className='mv-input' maxLength={24} autoComplete='off' autoCapitalize='characters' spellCheck='false'
                        placeholder={form.audience === 'CODE' ? 'e.g. DIWALI20' : 'Leave empty to make one'}
                        value={form.code} onChange={field('code', v => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
                    <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm'
                        onClick={() => { setForm(f => ({ ...f, code: randomCode(f.name) })); setFormError('') }}>
                        <HiOutlineRefresh aria-hidden='true' /> Generate
                    </button>
                </div>
                <p className='mv-field-hint'>Letters and numbers only. Shoppers can type it in any case.</p>
            </div>

            <div className='mv-form-grid mv-cpn-two'>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='cpn-start'>Starts <span className='mv-label-optional'>(optional)</span></label>
                    <input id='cpn-start' type='date' className='mv-input' value={form.startsOn} onChange={field('startsOn')} />
                </div>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='cpn-end'>Ends <span className='mv-label-optional'>(optional)</span></label>
                    <input id='cpn-end' type='date' className='mv-input' value={form.endsOn}
                        min={form.startsOn || (isEdit ? undefined : todayIso())} onChange={field('endsOn')} />
                </div>
            </div>

            <div className='mv-form-grid mv-cpn-two'>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='cpn-limit'>Total uses <span className='mv-label-optional'>(optional)</span></label>
                    <input id='cpn-limit' className='mv-input' inputMode='numeric' autoComplete='off' placeholder='No limit'
                        value={form.usageLimit} onChange={field('usageLimit', v => digits(v, false).slice(0, 7))} />
                    <p className='mv-field-hint'>Each shopper can use it once.</p>
                </div>
                <div className='mv-field'>
                    <span className='mv-label'>Status</span>
                    <ActiveSwitch checked={form.active} label='Coupon is on'
                        onChange={(active) => setForm(f => ({ ...f, active }))} />
                </div>
            </div>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='cpn-desc'>Note for shoppers <span className='mv-label-optional'>(optional)</span></label>
                <textarea id='cpn-desc' className='mv-textarea mv-cpn-desc' maxLength={255} placeholder='e.g. Our festive thank-you'
                    value={form.description} onChange={field('description')} />
            </div>

            {isEdit &&
                <section className='mv-cpn-usedby' aria-label='Used by'>
                    <h3 className='mv-cpn-usedby-title'>Used by</h3>
                    {usedBy === null ? <Skeleton height={44} radius='10px' /> :
                        usedBy.length === 0 ? <p className='mv-cpn-usedby-empty'>Nobody has used this coupon yet.</p> :
                            <ul className='mv-cpn-usedby-list'>
                                {usedBy.map((r, i) =>
                                    <li key={i} className='mv-cpn-usedby-row'>
                                        <span className='mv-user-avatar mv-role-customer' aria-hidden='true'>{initial(r.userName)}</span>
                                        <span className='mv-cpn-usedby-text'>
                                            <span className='mv-cpn-usedby-name'>{r.userName || r.email || 'Shopper'}</span>
                                            <span className='mv-cpn-usedby-sub'>
                                                {r.status === 'USED' ?
                                                    (r.orderId ? <button type='button' className='mv-link-btn'
                                                        onClick={() => history.push('/orders/admin/view/' + r.orderId)}>Order {r.orderNo || '#' + r.orderId}</button>
                                                        : (r.orderNo || 'Paid order')) :
                                                    'Paying now'}
                                                {r.at && ` · ${formatDay(String(r.at).slice(0, 10))}`}
                                            </span>
                                        </span>
                                        <span className='mv-cpn-usedby-amount'>−{formatRupee(r.discount)}</span>
                                    </li>)}
                            </ul>}
                </section>}

            {formError && <div className='mv-alert mv-alert-error' role='alert'>{formError}</div>}
        </form>
    )

    const canDelete = isEdit && Number(editing.takenCount) === 0
    const footer = (
        <>
            {canDelete &&
                <button type='button' className='mv-icon-btn mv-inv-delete' aria-label='Delete' title='Delete' disabled={saving}
                    onClick={() => setConfirmDelete(editing)}>
                    <HiOutlineTrash />
                </button>}
            <span className='mv-panel-spacer' />
            {!isEdit &&
                <button type='button' className='mv-btn mv-btn-secondary mv-cpn-another' disabled={saving} onClick={() => save(true)}>
                    Save &amp; new
                </button>}
            <button type='submit' form='cpn-form' className='mv-btn mv-btn-primary' disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create coupon'}
            </button>
        </>
    )

    const menuFor = (c) => (
        <div className='mv-exp-menu-wrap mv-cpn-menu-wrap'>
            <button type='button' className='mv-icon-btn' aria-label={`More for ${c.name}`} aria-haspopup='menu'
                aria-expanded={menuId === c.id} onClick={(e) => { e.stopPropagation(); setMenuId(menuId === c.id ? null : c.id) }}>
                <HiOutlineDotsVertical />
            </button>
            {menuId === c.id &&
                <div className='mv-exp-menu' role='menu' onClick={(e) => e.stopPropagation()}>
                    <button type='button' role='menuitem' onClick={() => openEditor('edit', c)}>
                        <HiOutlinePencil aria-hidden='true' /> Edit
                    </button>
                    <button type='button' role='menuitem' onClick={() => openEditor('duplicate', c)}>
                        <HiOutlineDuplicate aria-hidden='true' /> Duplicate
                    </button>
                    <button type='button' role='menuitem' onClick={() => copy(c)}>
                        <HiOutlineClipboardCopy aria-hidden='true' /> Copy code
                    </button>
                    {c.audience === 'CODE' &&
                        <a role='menuitem' href={shareLink(c)} target='_blank' rel='noopener noreferrer' onClick={() => setMenuId(null)}>
                            <FaWhatsapp aria-hidden='true' /> Share on WhatsApp
                        </a>}
                    {Number(c.takenCount) === 0 &&
                        <button type='button' role='menuitem' className='is-danger' onClick={() => { setMenuId(null); setConfirmDelete(c) }}>
                            <HiOutlineTrash aria-hidden='true' /> Delete
                        </button>}
                </div>}
        </div>
    )

    return (
        <main className='mv-page mv-cpn'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Coupons</h1>
                    <p className='mv-page-sub'>Treat shoppers to a little something: applied for them, or shared as a code.</p>
                </div>
                <button type='button' className='mv-btn mv-btn-primary mv-btn-sm' onClick={() => openEditor('new')}>
                    <HiOutlinePlus aria-hidden='true' /> New coupon
                </button>
            </div>

            <div className='mv-cpn-tiles'>
                <Tile icon={<HiOutlineCheckCircle />} label='Active now' tone='is-primary' loading={!list}
                    value={list ? list.activeCount : ''} />
                <Tile icon={<HiOutlineTicket />} label='Used this month' tone='is-accent' loading={!list}
                    value={list ? list.usedThisMonth : ''} />
                <Tile icon={<HiOutlineCurrencyRupee />} label='Saved by shoppers this month' tone='is-success' loading={!list}
                    value={list ? formatRupee(list.discountThisMonth || 0) : ''} />
            </div>

            <div className='mv-cpn-bar-row'>
                <div className='mv-chip-row' role='group' aria-label='Show'>
                    {FILTERS.map(f =>
                        <button type='button' key={f.key} className={status === f.key ? 'mv-filter-chip active' : 'mv-filter-chip'}
                            aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>{f.label}</button>)}
                </div>
                <div className='mv-search mv-cpn-search'>
                    <HiOutlineSearch aria-hidden='true' />
                    <input type='search' className='mv-input' placeholder='Search name or code' aria-label='Search coupons'
                        value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>

            {list && coupons.length > 0 &&
                <div className='mv-cpn-list-head'>
                    <span>{coupons.length} {coupons.length === 1 ? 'coupon' : 'coupons'}{filtered && list.totalCount !== coupons.length ? ` of ${list.totalCount}` : ''}</span>
                    {filtered && <button type='button' className='mv-link-btn' onClick={clearFilters}><HiX aria-hidden='true' /> Clear filters</button>}
                </div>}

            {listError && !list &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load coupons'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={load} />}

            {!listError && !list && <ListSkeleton />}

            {list && coupons.length === 0 && (filtered ?
                <EmptyState icon={<HiOutlineSearch />} title='Nothing matches'
                    text='Try another search or filter.' actionLabel='Clear filters' onAction={clearFilters} /> :
                <EmptyState icon={<HiOutlineTicket />} title='No coupons yet'
                    text='Make a welcome offer for everyone, a thank-you for loyal shoppers, or a code to share on WhatsApp.'
                    actionLabel='New coupon' onAction={() => openEditor('new')} />)}

            {coupons.length > 0 &&
                <ul className='mv-cpn-grid'>
                    {coupons.map(c =>
                        <Ticket key={c.id} coupon={c} flash={flashId === c.id} onOpen={() => openEditor('edit', c)}
                            onCopy={() => copy(c)} menu={menuFor(c)}
                            toggle={<ActiveSwitch checked={c.active !== false} busy={busyId === c.id} label={`${c.name} is on`}
                                onChange={(active) => toggleActive(c, active)} />} />)}
                </ul>}

            <SidePanel open={editor != null} title={editorTitle} onClose={closeEditor} busy={saving || confirmDelete != null} footer={footer}>
                {editor && renderForm()}
            </SidePanel>

            {confirmDelete &&
                <Modal title='Delete this coupon?' onClose={() => !deleting && setConfirmDelete(null)} busy={deleting}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' disabled={deleting} onClick={() => setConfirmDelete(null)}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger-solid' disabled={deleting} onClick={doDelete}>
                            {deleting ? 'Deleting…' : 'Delete'}
                        </button>
                    </>}>
                    <p className='mv-modal-text'>
                        <strong>{confirmDelete.name}</strong> ({confirmDelete.code}) has not been used, so it can be deleted. This can't be undone.
                    </p>
                    {confirmDelete.error && <div className='mv-alert mv-alert-error' role='alert'>{confirmDelete.error}</div>}
                </Modal>}

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default Coupons
